/**
 * Screen 7 — Run Now modal. Fires a one-shot campaign bypassing the cron
 * schedule. Optional per-fire overrides (briefs count, force full run) are
 * snapshotted onto the campaign row on the server so they only apply to
 * this fire.
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Zap } from 'lucide-react';
import { FormDialog } from '@/components/ui/form-dialog';
import { TextField } from '@/components/ui/form-field';
import { summarizeApiError } from '@/lib/api';
import { useRunAutoCampaignNow } from '@/features/auto-campaign/mutations';

export interface RunNowDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function RunNowDialog({
  projectId,
  open,
  onOpenChange,
}: RunNowDialogProps): JSX.Element {
  const runNow = useRunAutoCampaignNow(projectId);
  const navigate = useNavigate();
  const [briefs, setBriefs] = useState<string>('3');
  const [forceFullRun, setForceFullRun] = useState(false);

  async function submit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    try {
      const body: { briefs_per_fire?: number; force_full_run?: boolean } = {};
      const briefsNum = Number.parseInt(briefs, 10);
      if (!Number.isNaN(briefsNum)) body.briefs_per_fire = briefsNum;
      if (forceFullRun) body.force_full_run = true;

      const res = await runNow.mutateAsync(body);
      toast.success('Auto-Campaign fire queued', {
        description: 'Opening the fire — the digest also goes to Telegram when it completes.',
      });
      onOpenChange(false);
      // Take the user straight to the fire they just started so they can
      // watch the timeline instead of hunting for it in the history table.
      await navigate({
        to: '/campaigns/auto/fires/$fireId',
        params: { fireId: res.campaign_id },
      });
    } catch (err) {
      const s = summarizeApiError(err, {
        fallbackTitle: 'Could not start fire',
      });
      toast.error(s.title, { description: s.description });
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<Zap className="h-5 w-5 text-primary" />}
      title="Run Auto-Pilot now"
      description="Runs the whole thing straight away instead of waiting for the next scheduled run. You'll get the report in Telegram when it finishes — usually 1–3 hours."
      submitLabel="Start run"
      submitting={runNow.isPending}
      onSubmit={submit}
    >
      <TextField
        label="How many topics to work on"
        type="number"
        min={1}
        max={15}
        value={briefs}
        onChange={(e) => setBriefs(e.target.value)}
        hint="For each topic you get: who currently ranks for it, a content brief, and ready-to-post drafts for LinkedIn, Quora, Reddit and Medium. Start with 1 if you just want to see the output."
      />
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-primary"
          checked={forceFullRun}
          onChange={(e) => setForceFullRun(e.target.checked)}
        />
        <span>
          <span className="font-medium">Re-scan the site and redo keyword research</span>
          <span className="block text-xs text-muted-foreground">
            Off by default: if the site was scanned in the last 15 days, that
            scan is reused to save time. Tick this to start from scratch.
          </span>
        </span>
      </label>
    </FormDialog>
  );
}
